import { useCallback, useEffect, useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import {
	Calendar,
	Check,
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	Clock3,
	Search,
	Upload,
	X,
} from "lucide-react";

import EhrCsvUploadModal from "../components/EhrCsvUploadModal";
import EhrImportDetailsDrawer from "../components/EhrImportDetailsDrawer";
import EhrUploadHistoryDrawer from "../components/EhrUploadHistoryDrawer";
import { getActiveValidationImportRun, getUsers, getValidationImportDetails, getValidationImportRun, getValidationMetrics, getValidationRecords, getValidationUploads } from "../config/url";
import { getConfigWithToken } from "../config/config";
import { buildUploadHistoryQuery } from "../utils/ehrUploadHistory";
import {
	clearActiveImportRun,
	dismissPendingValidationBanner,
	isImportRunActive,
	isImportRunFinished,
	loadActiveImportRun,
	loadPendingValidationBanner,
	markImportCompletionHandled,
	notifyImportComplete,
	saveActiveImportRun,
	savePendingValidationBanner,
	wasImportCompletionHandled,
} from "../utils/ehrImportRun";
import { formatDateUS } from "../utils/visitDisplay";
import {
	buildValidationQueryParams,
	formatUploadBannerTimestamp,
	formatUploadDateRange,
	formatValidationFlagLabel,
	getValidationStatusClass,
	getValidationStatusLabel,
	hasActiveValidationFilters,
} from "../utils/validationDisplay";
import classes from "./Validate.module.css";

const BRAND = "#064260";
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const emptyMetrics = {
	dataMismatch: 0,
	hasUpload: false,
	lastUpload: null,
	lastUploadDate: null,
	matchedWithIssues: 0,
	missingInTeleTrace: 0,
	totalEhrVisits: 0,
	unableToValidate: 0,
	unconfirmed: 0,
	verified: 0,
	verifiedRate: 0,
};

const Validate = () => {
	const currentUser = useSelector((state) => state?.user);
	const token = currentUser?.user?.token || localStorage.getItem("user_id");
	const organizationId =
		currentUser?.user?.organizationId ||
		localStorage.getItem("organizationId") ||
		localStorage.getItem("businessId") ||
		"";
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const uploadIdFilter = searchParams.get("uploadId") || "";
	const importRunParam = searchParams.get("importRun") || "";

	const [search, setSearch] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");
	const [providerFilter, setProviderFilter] = useState("");
	const [customStartDate, setCustomStartDate] = useState("");
	const [customEndDate, setCustomEndDate] = useState("");
	const [records, setRecords] = useState([]);
	const [metrics, setMetrics] = useState(emptyMetrics);
	const [providers, setProviders] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [totalPages, setTotalPages] = useState(1);
	const [showUploadModal, setShowUploadModal] = useState(false);
	const [showUploadHistory, setShowUploadHistory] = useState(false);
	const [uploadHistory, setUploadHistory] = useState([]);
	const [uploadHistoryLoading, setUploadHistoryLoading] = useState(false);
	const [uploadHistoryPagination, setUploadHistoryPagination] = useState({
		limit: 10,
		page: 1,
		totalPages: 1,
	});
	const [uploadHistoryFilters, setUploadHistoryFilters] = useState({
		endDate: "",
		startDate: "",
		status: "",
		uploadedBy: "",
	});
	const [uploadHistoryUploaders, setUploadHistoryUploaders] = useState([]);
	const [showImportDetails, setShowImportDetails] = useState(false);
	const [importDetails, setImportDetails] = useState(null);
	const [importDetailsLoading, setImportDetailsLoading] = useState(false);
	const [importDetailsError, setImportDetailsError] = useState(null);
	const [importFilterMeta, setImportFilterMeta] = useState(null);
	const [validationBannerDismissed, setValidationBannerDismissed] = useState(false);
	const [validationCompleteSummary, setValidationCompleteSummary] = useState(null);
	const [activeImportRun, setActiveImportRun] = useState(null);
	const [trackingImportRun, setTrackingImportRun] = useState(null);

	const requestConfig = useMemo(
		() => ({
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
				...(token ? getConfigWithToken(token).headers : {}),
				...(organizationId ? { "x-organization-id": organizationId } : {}),
			},
		}),
		[organizationId, token],
	);

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
		return () => clearTimeout(timer);
	}, [search]);

	useEffect(() => {
		const urlStatus = searchParams.get("status") || "";
		setStatusFilter(urlStatus);
	}, [searchParams]);

	useEffect(() => {
		setCurrentPage(1);
	}, [
		debouncedSearch,
		statusFilter,
		providerFilter,
		customStartDate,
		customEndDate,
		rowsPerPage,
		uploadIdFilter,
	]);

	useEffect(() => {
		const loadProviders = async () => {
			try {
				const response = await axios.get(getUsers, {
					...requestConfig,
					params: {
						limit: 200,
						page: 1,
						status: "Active",
						...(organizationId ? { organizationId } : {}),
						role: "Provider",
					},
				});
				const rows = response?.data?.data || [];
				const list = (Array.isArray(rows) ? rows : [])
					.map((user) => ({
						id: user._id,
						name:
							[user.firstname, user.lastname].filter(Boolean).join(" ").trim() ||
							user.userId ||
							user.email ||
							"Provider",
					}))
					.filter((provider) => provider.id)
					.sort((a, b) => a.name.localeCompare(b.name));
				setProviders(list);
			} catch {
				setProviders([]);
			}
		};
		loadProviders();
	}, [organizationId, requestConfig]);

	const loadMetrics = useCallback(async () => {
		try {
			const hasCustomDates = Boolean(customStartDate || customEndDate);
			const response = await axios.get(getValidationMetrics, {
				...requestConfig,
				params: {
					...(organizationId ? { organizationId } : {}),
					...(hasCustomDates ? { dateRange: "custom" } : {}),
					...(customStartDate ? { startDate: customStartDate } : {}),
					...(customEndDate ? { endDate: customEndDate } : {}),
					...(uploadIdFilter ? { uploadId: uploadIdFilter } : {}),
				},
			});
			setMetrics(response?.data?.data || emptyMetrics);
		} catch {
			setMetrics(emptyMetrics);
			toast.error("Unable to load validation metrics.");
		}
	}, [customEndDate, customStartDate, organizationId, requestConfig, uploadIdFilter]);

	const loadRecords = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const response = await axios.get(getValidationRecords, {
				...requestConfig,
				params: buildValidationQueryParams({
					currentPage,
					dateRange: customStartDate || customEndDate ? "custom" : "",
					debouncedSearch,
					endDate: customEndDate,
					organizationId,
					providerFilter,
					rowsPerPage,
					startDate: customStartDate,
					statusFilter,
					uploadId: uploadIdFilter,
				}),
			});

			if (response.data?.success === false) {
				throw new Error(response.data?.message || "Failed to load validation records.");
			}

			const rows = response?.data?.data || [];
			const pagination = response?.data?.pagination || {};
			setRecords(Array.isArray(rows) ? rows : []);
			setTotalPages(pagination.totalPages || 1);
		} catch (err) {
			setRecords([]);
			setTotalPages(1);
			setError(
				err?.response?.data?.message ||
					err?.message ||
					"Unable to load validation records. Please try again.",
			);
		} finally {
			setLoading(false);
		}
	}, [
		currentPage,
		customEndDate,
		customStartDate,
		debouncedSearch,
		organizationId,
		providerFilter,
		requestConfig,
		rowsPerPage,
		statusFilter,
		uploadIdFilter,
	]);

	useEffect(() => {
		loadMetrics();
	}, [loadMetrics]);

	useEffect(() => {
		loadRecords();
	}, [loadRecords]);

	const hasFilters = hasActiveValidationFilters({
		debouncedSearch,
		endDate: customEndDate,
		providerFilter,
		startDate: customStartDate,
		statusFilter,
		uploadId: uploadIdFilter,
	});

	const buildSearchParams = ({ importRun, status, uploadId } = {}) => {
		const nextParams = {};
		if (status) nextParams.status = status;
		if (uploadId) nextParams.uploadId = String(uploadId);
		if (importRun) nextParams.importRun = String(importRun);
		return nextParams;
	};

	const handleStatusFilter = (status) => {
		setStatusFilter(status);
		setSearchParams(
			buildSearchParams({
				status: status || undefined,
				uploadId: uploadIdFilter || undefined,
			}),
		);
	};

	const handleClearImportFilter = () => {
		setImportFilterMeta(null);
		setSearchParams(buildSearchParams({ status: statusFilter || undefined }));
	};

	const openRecord = (record) => {
		if (!record?.id) return;
		navigate(`/validate/${record.id}`);
	};

	const handleRowKeyDown = (event, record) => {
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			openRecord(record);
		}
	};

	const loadUploadHistory = async ({
		endDate = uploadHistoryFilters.endDate,
		page = uploadHistoryPagination.page,
		rowsPerPage = uploadHistoryPagination.limit,
		startDate = uploadHistoryFilters.startDate,
		status = uploadHistoryFilters.status,
		uploadedBy = uploadHistoryFilters.uploadedBy,
	} = {}) => {
		setUploadHistoryLoading(true);
		try {
			const response = await axios.get(getValidationUploads, {
				...requestConfig,
				params: {
					...buildUploadHistoryQuery({
						endDate,
						page,
						rowsPerPage,
						startDate,
						status,
						uploadedBy,
					}),
					...(organizationId ? { organizationId } : {}),
				},
			});
			setUploadHistory(Array.isArray(response?.data?.data) ? response.data.data : []);
			setUploadHistoryUploaders(Array.isArray(response?.data?.filters?.uploaders) ? response.data.filters.uploaders : []);
			setUploadHistoryPagination({
				limit: response?.data?.pagination?.limit || rowsPerPage,
				page: response?.data?.pagination?.page || page,
				totalPages: response?.data?.pagination?.totalPages || 1,
			});
		} catch {
			setUploadHistory([]);
			setUploadHistoryUploaders([]);
			toast.error("Unable to load upload history.");
		} finally {
			setUploadHistoryLoading(false);
		}
	};

	const handleOpenUploadHistory = async () => {
		setShowUploadHistory(true);
		await loadUploadHistory({ page: 1 });
	};

	const handleUploadHistoryFilterChange = async (key, value) => {
		const nextFilters = { ...uploadHistoryFilters, [key]: value };
		setUploadHistoryFilters(nextFilters);
		await loadUploadHistory({
			endDate: nextFilters.endDate,
			page: 1,
			rowsPerPage: uploadHistoryPagination.limit,
			startDate: nextFilters.startDate,
			status: nextFilters.status,
			uploadedBy: nextFilters.uploadedBy,
		});
	};

	const handleUploadHistoryPageChange = async (page) => {
		await loadUploadHistory({ page });
	};

	const handleUploadHistoryRowsPerPageChange = async (rowsPerPage) => {
		setUploadHistoryPagination((previous) => ({ ...previous, limit: rowsPerPage, page: 1 }));
		await loadUploadHistory({ page: 1, rowsPerPage });
	};

	const loadImportDetails = useCallback(
		async (runId) => {
			if (!runId || !organizationId) return;

			setImportDetailsLoading(true);
			setImportDetailsError(null);

			try {
				const response = await axios.get(getValidationImportDetails(runId), {
					...requestConfig,
					params: organizationId ? { organizationId } : undefined,
				});
				setImportDetails(response?.data?.data || null);
			} catch (loadError) {
				setImportDetails(null);
				setImportDetailsError(
					loadError?.response?.data?.message || "Import details could not be loaded.",
				);
			} finally {
				setImportDetailsLoading(false);
			}
		},
		[organizationId, requestConfig],
	);

	const handleOpenImportDetails = async (runId) => {
		setShowUploadHistory(false);
		setShowImportDetails(true);
		setImportDetails(null);
		await loadImportDetails(runId);
	};

	const handleBackToUploadHistory = () => {
		setShowImportDetails(false);
		setImportDetails(null);
		setImportDetailsError(null);
		setSearchParams(buildSearchParams({ status: statusFilter || undefined, uploadId: uploadIdFilter || undefined }));
		setShowUploadHistory(true);
		loadUploadHistory({ page: 1 });
	};

	const handleCloseImportDetails = () => {
		setShowImportDetails(false);
		setImportDetails(null);
		setImportDetailsError(null);
		setSearchParams(buildSearchParams({ status: statusFilter || undefined, uploadId: uploadIdFilter || undefined }));
	};

	const handleViewImportResults = () => {
		if (!importDetails?.uploadId) return;

		setImportFilterMeta({
			displayId: importDetails.displayId,
			fileName: importDetails.fileName,
		});
		setShowImportDetails(false);
		setImportDetails(null);
		setImportDetailsError(null);
		setCurrentPage(1);
		setSearchParams(
			buildSearchParams({
				status: statusFilter || undefined,
				uploadId: importDetails.uploadId,
			}),
		);
	};

	useEffect(() => {
		if (!importRunParam) return;

		const openFromDeepLink = async () => {
			await handleOpenImportDetails(importRunParam);
			setSearchParams(
				buildSearchParams({
					status: statusFilter || undefined,
					uploadId: uploadIdFilter || undefined,
				}),
			);
		};

		openFromDeepLink();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [importRunParam]);

	const loadValidationCompleteBanner = useCallback(
		async (run) => {
			if (!run?.runId || run.status !== "completed" || !organizationId) return;

			try {
				const response = await axios.get(getValidationImportDetails(run.runId), {
					...requestConfig,
					params: organizationId ? { organizationId } : undefined,
				});
				const details = response?.data?.data;
				const summary = details?.validationSummary;
				if (!summary?.totalRecords) return;

				const matchedCount = (summary.verifiedClean || 0) + (summary.matchedWithIssues || 0);
				const requireReviewCount =
					(summary.dataMismatch || 0) +
					(summary.missingInTeleTrace || 0) +
					(summary.unconfirmed || 0) +
					(summary.unableToValidate || 0);

				setValidationCompleteSummary({
					displayId: details.displayId,
					fileName: details.fileName,
					matchedCount,
					requireReviewCount,
					runId: run.runId,
					totalRecords: summary.totalRecords,
					uploadId: details.uploadId,
				});
				setValidationBannerDismissed(false);
				savePendingValidationBanner(organizationId, run.runId);
			} catch {
				// Banner counts require validation summary from the import details API.
			}
		},
		[organizationId, requestConfig],
	);

	const handleImportCompletion = useCallback(
		async (run) => {
			if (!run?.runId || run.status !== "completed" || !organizationId) return false;
			if (wasImportCompletionHandled(organizationId, run.runId)) {
				await loadValidationCompleteBanner(run);
				return false;
			}

			markImportCompletionHandled(organizationId, run.runId);
			clearActiveImportRun(organizationId);
			setActiveImportRun(null);
			setTrackingImportRun(null);
			notifyImportComplete(run.fileName);
			toast.success(
				`${(run.importedRecords || 0) + (run.updatedRecords || 0)} EHR records processed successfully.`,
			);
			await Promise.all([loadMetrics(), loadRecords()]);
			await loadValidationCompleteBanner(run);
			return true;
		},
		[loadMetrics, loadRecords, loadValidationCompleteBanner, organizationId],
	);

	const handleUploadClick = () => {
		if (!organizationId) {
			toast.error("Organization context is required.");
			return;
		}
		setShowUploadModal(true);
	};

	const handleUploadSuccess = async (completedRun) => {
		await handleImportCompletion(completedRun);
	};

	const handleDismissValidationBanner = () => {
		setValidationBannerDismissed(true);
		dismissPendingValidationBanner(organizationId);
	};

	const handleViewLatestValidationResults = () => {
		const uploadId = validationCompleteSummary?.uploadId;
		if (!uploadId) return;

		setImportFilterMeta({
			displayId: validationCompleteSummary.displayId,
			fileName: validationCompleteSummary.fileName,
		});
		setCurrentPage(1);
		setSearchParams(
			buildSearchParams({
				status: statusFilter || undefined,
				uploadId,
			}),
		);
		handleDismissValidationBanner();
	};

	useEffect(() => {
		if (!organizationId) return;

		const pendingBanner = loadPendingValidationBanner(organizationId);
		if (!pendingBanner?.runId) return;

		setValidationBannerDismissed(false);
		loadValidationCompleteBanner({ runId: pendingBanner.runId, status: "completed" });
	}, [loadValidationCompleteBanner, organizationId]);

	useEffect(() => {
		if (!organizationId || !token) return undefined;

		let cancelled = false;

		const pollImportRun = async () => {
			try {
				const saved = loadActiveImportRun(organizationId);
				let run = null;

				if (saved?.runId) {
					const response = await axios.get(getValidationImportRun(saved.runId), requestConfig);
					run = response?.data?.data || null;
				} else {
					const response = await axios.get(getActiveValidationImportRun, requestConfig);
					run = response?.data?.data || null;
					if (run?.runId) {
						saveActiveImportRun(organizationId, run.runId);
					}
				}

				if (cancelled || !run) {
					if (!cancelled) setActiveImportRun(null);
					return;
				}

				if (isImportRunActive(run.status)) {
					setActiveImportRun(run);
					return;
				}

				if (isImportRunFinished(run.status)) {
					if (run.status === "completed") {
						await handleImportCompletion(run);
					} else {
						clearActiveImportRun(organizationId);
						setActiveImportRun(null);
						toast.error(run.errorMessage || "EHR import failed.");
					}
				}
			} catch {
				if (!cancelled) setActiveImportRun(null);
			}
		};

		pollImportRun();
		const intervalId = window.setInterval(pollImportRun, 3000);

		return () => {
			cancelled = true;
			window.clearInterval(intervalId);
		};
	}, [handleImportCompletion, organizationId, requestConfig, token]);

	const renderStatusBadge = (status) => {
		const statusClass = getValidationStatusClass(status);
		return (
			<span className={classes[statusClass]}>
				<span aria-hidden="true" className={classes.statusDot} />
				{getValidationStatusLabel(status)}
			</span>
		);
	};

	const renderFlags = (record) => {
		const label = formatValidationFlagLabel(record.flagCount);
		if (label === "—") return <span className={classes.mutedCell}>—</span>;
		return <span className={classes.flagText}>{label}</span>;
	};

	const getRowClassName = (status) => {
		if (status === "Data Mismatch") return classes.rowIssues;
		if (status === "Missing in TeleTrace") return classes.rowMissing;
		if (status === "Unable to Validate") return classes.rowUnable;
		return "";
	};

	const buildDataCurrentBannerMeta = (upload) => {
		if (!upload) return null;

		const segments = [];
		const timestamp = formatUploadBannerTimestamp(upload.uploadedAt);
		if (timestamp) {
			segments.push(`Last upload ${timestamp.replace(" · ", " • ")}`);
		}

		if (upload.uploadedByName) {
			segments.push(upload.uploadedByName);
		}

		const dateRange = formatUploadDateRange(upload.dateRangeStart, upload.dateRangeEnd);
		if (dateRange) {
			segments.push(dateRange);
		}

		return segments.length ? segments.join(" • ") : null;
	};

	const lastUpload = metrics.lastUpload;
	const dataCurrentBannerMeta = buildDataCurrentBannerMeta(lastUpload);
	const showValidationCompleteBanner =
		Boolean(validationCompleteSummary) && !validationBannerDismissed && !activeImportRun && !uploadIdFilter;
	const showDataCurrentBanner =
		metrics.hasUpload && !showValidationCompleteBanner && !activeImportRun && !uploadIdFilter;

	return (
		<div className={classes.page}>
			<div className={classes.pageHeader}>
				<div className={classes.titleBlock}>
					<h1 className={classes.title}>Validate</h1>
					<p className={classes.subtitle}>Compare EHR community visit records against TeleTrace.</p>
				</div>
				<div className={classes.headerActions}>
					<button className={classes.outlineButton} onClick={handleOpenUploadHistory} type="button">
						<Clock3 size={15} strokeWidth={2} />
						Upload History
					</button>
					<button className={classes.primaryButton} onClick={handleUploadClick} type="button">
						<Upload size={15} strokeWidth={2} />
						Upload EHR CSV
					</button>
				</div>
			</div>

			{activeImportRun && !showUploadModal ? (
				<div className={classes.importProgressBanner} role="status">
					<div className={classes.importProgressCopy}>
						<div className={classes.importProgressTitle}>Importing and validating EHR visits…</div>
						<div className={classes.importProgressMeta}>
							{activeImportRun.fileName ? `${activeImportRun.fileName} · ` : ""}
							{Number(activeImportRun.progressPercent || 0)}% complete
						</div>
					</div>
					<button
						className={classes.importProgressButton}
						onClick={() => {
							setTrackingImportRun(activeImportRun);
							setShowUploadModal(true);
						}}
						type="button">
						View Progress
					</button>
				</div>
			) : null}

			{showValidationCompleteBanner ? (
				<div className={classes.validationCompleteBanner} role="status">
					<div className={classes.validationCompleteIcon}>
						<Check aria-hidden="true" size={18} strokeWidth={2.5} />
					</div>
					<div className={classes.validationCompleteCopy}>
						<div className={classes.validationCompleteTitle}>
							Validation complete — {validationCompleteSummary.matchedCount.toLocaleString("en-US")} of{" "}
							{validationCompleteSummary.totalRecords.toLocaleString("en-US")} records matched.{" "}
							{validationCompleteSummary.requireReviewCount.toLocaleString("en-US")} require review.
						</div>
						<div className={classes.validationCompleteMeta}>
							EHR CSV upload fully validated. Flagged mismatches are updated below.
						</div>
					</div>
					<div className={classes.validationCompleteActions}>
						<button
							className={classes.validationCompleteButton}
							onClick={handleViewLatestValidationResults}
							type="button">
							View Results
						</button>
						<button
							aria-label="Dismiss validation banner"
							className={classes.validationCompleteDismiss}
							onClick={handleDismissValidationBanner}
							type="button">
							<X aria-hidden="true" size={18} strokeWidth={2} />
						</button>
					</div>
				</div>
			) : null}

			{showDataCurrentBanner ? (
				<div className={classes.statusBanner} role="status">
					<span aria-hidden="true" className={classes.statusBannerDot} />
					<div className={classes.statusBannerContent}>
						<div className={classes.statusBannerTitle}>Data is current.</div>
						{dataCurrentBannerMeta ? (
							<div className={classes.statusBannerMeta}>{dataCurrentBannerMeta}</div>
						) : null}
					</div>
				</div>
			) : null}

			{uploadIdFilter ? (
				<div className={classes.importFilterBanner} role="status">
					<div className={classes.importFilterCopy}>
						<div className={classes.importFilterTitle}>
							{importFilterMeta?.displayId
								? `Showing results for ${importFilterMeta.displayId}`
								: "Showing results for selected import"}
						</div>
						{importFilterMeta?.fileName ? (
							<div className={classes.importFilterMeta}>{importFilterMeta.fileName}</div>
						) : null}
					</div>
					<button className={classes.importFilterButton} onClick={handleClearImportFilter} type="button">
						Clear Import Filter
					</button>
				</div>
			) : null}

			{metrics.hasUpload ? (
				<div className={classes.toolbar}>
					<div className={classes.filters}>
						<div className={classes.searchWrap}>
							<Search aria-hidden="true" className={classes.searchIcon} size={15} strokeWidth={2} />
							<input
								aria-label="Search validation records"
								className={classes.searchInput}
								onChange={(event) => setSearch(event.target.value)}
								placeholder="Search visits..."
								type="search"
								value={search}
							/>
						</div>

						<div className={classes.selectWrap}>
							<select
								aria-label="Filter by validation status"
								className={classes.filterSelect}
								onChange={(event) => handleStatusFilter(event.target.value)}
								value={statusFilter}>
								<option value="">All Statuses</option>
								<option value="Verified">Verified</option>
								<option value="Matched with Issues">Matched with Issues</option>
								<option value="Unconfirmed">Unconfirmed</option>
								<option value="Data Mismatch">Data Mismatch</option>
								<option value="Missing in TeleTrace">Missing in TeleTrace</option>
								<option value="Unable to Validate">Unable to Validate</option>
							</select>
							<ChevronDown aria-hidden="true" className={classes.selectIcon} size={15} strokeWidth={2} />
						</div>

						<div className={classes.selectWrap}>
							<select
								aria-label="Filter by provider"
								className={classes.filterSelect}
								onChange={(event) => setProviderFilter(event.target.value)}
								value={providerFilter}>
								<option value="">All Providers</option>
								{providers.map((provider) => (
									<option key={provider.id} value={provider.id}>
										{provider.name}
									</option>
								))}
							</select>
							<ChevronDown aria-hidden="true" className={classes.selectIcon} size={15} strokeWidth={2} />
						</div>

						<div className={classes.dateRangeField}>
							<Calendar aria-hidden="true" className={classes.dateRangeIcon} size={15} strokeWidth={2} />
							<div className={classes.dateRangeInputs}>
								<input
									aria-label="Filter start date"
									className={classes.dateRangePart}
									onChange={(event) => setCustomStartDate(event.target.value)}
									type="date"
									value={customStartDate}
								/>
								<span className={classes.dateRangeSeparator}>-</span>
								<input
									aria-label="Filter end date"
									className={classes.dateRangePart}
									min={customStartDate || undefined}
									onChange={(event) => setCustomEndDate(event.target.value)}
									type="date"
									value={customEndDate}
								/>
							</div>
						</div>
					</div>
				</div>
			) : null}

			<div className={classes.card}>
				{loading ? (
					<div className={classes.stateBlock} role="status">
						<Spinner animation="border" style={{ color: BRAND }} />
						<p className={classes.stateText}>Loading validation records...</p>
					</div>
				) : error ? (
					<div className={classes.errorState} role="alert">
						<p className={classes.errorTitle}>Unable to load validation records</p>
						<p className={classes.stateText}>{error}</p>
						<button className={classes.retryButton} onClick={loadRecords} type="button">
							Try Again
						</button>
					</div>
				) : !metrics.hasUpload ? (
					<div className={classes.stateBlock}>
						<p className={classes.emptyTitle}>No EHR data uploaded</p>
						<p className={classes.stateText}>
							Upload an EHR visit report to compare records against TeleTrace community visits.
						</p>
					</div>
				) : records.length === 0 ? (
					<div className={classes.stateBlock}>
						<p className={classes.emptyTitle}>{hasFilters ? "No matching records" : "No validation records found"}</p>
						<p className={classes.stateText}>
							{hasFilters
								? "Try adjusting your search or filters."
								: "Validation records will appear here after an EHR upload."}
						</p>
					</div>
				) : (
					<>
						<div className={classes.tableWrap}>
							<table className={classes.table}>
								<colgroup>
									<col className={classes.colClient} />
									<col className={classes.colProvider} />
									<col className={classes.colDate} />
									<col className={classes.colId} />
									<col className={classes.colId} />
									<col className={classes.colFlags} />
									<col className={classes.colStatus} />
								</colgroup>
								<thead>
									<tr>
										<th>Client</th>
										<th>Provider</th>
										<th>Date</th>
										<th>EHR Visit ID</th>
										<th>TeleTrace Visit ID</th>
										<th>Flags</th>
										<th>Validation Status</th>
									</tr>
								</thead>
								<tbody>
									{records.map((record) => (
										<tr
											className={`${classes.row} ${getRowClassName(record.validationStatus)}`.trim()}
											key={record.id}
											onClick={() => openRecord(record)}
											onKeyDown={(event) => handleRowKeyDown(event, record)}
											role="link"
											tabIndex={0}>
											<td className={classes.clientName}>{record.clientName}</td>
											<td className={classes.providerName}>{record.providerName}</td>
											<td className={classes.dateCell}>{formatDateUS(record.dateOfService)}</td>
											<td className={classes.visitIdCell}>{record.ehrVisitId || "—"}</td>
											<td className={record.teletraceVisitId ? classes.visitIdCell : classes.mutedCell}>
												{record.teletraceVisitId || "—"}
											</td>
											<td>{renderFlags(record)}</td>
											<td className={classes.statusCell}>{renderStatusBadge(record.validationStatus)}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>

						<div className={classes.pagination}>
							<label className={classes.rowsPerPage}>
								<span>Rows per page</span>
								<div className={classes.rowsSelectWrap}>
									<select
										aria-label="Rows per page"
										className={classes.rowsSelect}
										onChange={(event) => setRowsPerPage(Number(event.target.value))}
										value={rowsPerPage}>
										{ROWS_PER_PAGE_OPTIONS.map((option) => (
											<option key={option} value={option}>
												{option}
											</option>
										))}
									</select>
									<ChevronDown
										aria-hidden="true"
										className={classes.rowsSelectIcon}
										size={14}
										strokeWidth={2}
									/>
								</div>
							</label>

							<div className={classes.pageControls}>
								<button
									aria-label="Previous page"
									className={classes.pageButton}
									disabled={currentPage <= 1}
									onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
									type="button">
									<ChevronLeft size={18} strokeWidth={2} />
								</button>
								<span className={classes.pageLabel}>
									Page {currentPage} of {totalPages}
								</span>
								<button
									aria-label="Next page"
									className={classes.pageButton}
									disabled={currentPage >= totalPages}
									onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
									type="button">
									<ChevronRight size={18} strokeWidth={2} />
								</button>
							</div>
						</div>
					</>
				)}
			</div>

			<EhrCsvUploadModal
				initialImportRun={trackingImportRun}
				isOpen={showUploadModal}
				onClose={() => {
					setShowUploadModal(false);
					setTrackingImportRun(null);
				}}
				onUploadSuccess={handleUploadSuccess}
				organizationId={organizationId}
				token={token}
				uploadedByName={
					[currentUser?.user?.firstname, currentUser?.user?.lastname].filter(Boolean).join(" ") ||
					currentUser?.user?.name ||
					""
				}
			/>

			{showUploadHistory ? (
				<EhrUploadHistoryDrawer
					filters={uploadHistoryFilters}
					historyRows={uploadHistory}
					isLoading={uploadHistoryLoading}
					onClose={() => setShowUploadHistory(false)}
					onFilterChange={handleUploadHistoryFilterChange}
					onOpenImport={handleOpenImportDetails}
					onPageChange={handleUploadHistoryPageChange}
					onRowsPerPageChange={handleUploadHistoryRowsPerPageChange}
					pagination={uploadHistoryPagination}
					uploaders={uploadHistoryUploaders}
				/>
			) : null}

			{showImportDetails ? (
				<EhrImportDetailsDrawer
					details={importDetails}
					error={importDetailsError}
					isLoading={importDetailsLoading}
					onBack={handleBackToUploadHistory}
					onClose={handleCloseImportDetails}
					onViewResults={handleViewImportResults}
				/>
			) : null}
		</div>
	);
};

export default Validate;
